import React, {
  FC,
  MouseEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { ColDef, ColGroupDef } from "ag-grid-community";
import { KeyboardArrowDown } from "@material-ui/icons";
import ArrowPopper from "components/app/ArrowPopper";
import { Divider, Grid } from "@material-ui/core";
import { COLLECTION_COLUMN, SUB_GROUP_MAPPING } from "constant";
import { classHOC } from "components/app/ClassWrapper";
import { Account } from "types/portfolio";

const labelConfig = [
  {
    colId: "invoiceArrMeetingMsgOnPrem",
    headerName: "Invoice ARR (On-Prem Meetings + Messaging)",
  },
  {
    colId: "invoiceArrCallingOnPrem",
    headerName: "Invoice ARR (On-Prem Calling)",
  },
  {
    colId: "invoiceArrContactCenterOnPrem",
    headerName: "Invoice ARR (On-Prem Contact Center)",
  },
  {
    colId: "invoiceArrDeviceOnPrem",
    headerName: "Invoice ARR (On-Prem Devices)",
  },
  {
    colId: "invoiceArrMeetingMsgCloud",
    headerName: "Invoice ARR (Cloud Meetings + Messaging)",
  },
  {
    colId: "invoiceArrCallingCloud",
    headerName: "Invoice ARR (Cloud Calling)",
  },
  {
    colId: "invoiceArrCloudWXCC",
    headerName: "Invoice ARR (Webex Contact Center)",
    subGroupHead: "Invoice ARR (Cloud Contact Center)",
  },
  {
    colId: "invoiceArrCloudWXCCAddon",
    headerName: "Invoice ARR (Webex Contact Center Add-Ons)",
    subGroupHeader: "Invoice ARR (Cloud Contact Center)",
  },
  {
    colId: "invoiceArrCloudWXCCDC",
    headerName: "Invoice ARR (Webex Contact Center Digital Channels)",
    subGroupHeader: "Invoice ARR (Cloud Contact Center)",
  },
  {
    colId: "invoiceArrCloudWXCCE",
    headerName: "Invoice ARR (Webex Contact Center Enterprise)",
    subGroupHeader: "Invoice ARR (Cloud Contact Center)",
  },
  { colId: "invoiceArrDeviceCloud", headerName: "Invoice ARR (Cloud Devices)" },
  {
    colId: "invoiceArrCPassCloud",
    headerName: "Invoice ARR (Cloud - CPAAS GPL)",
  },
  {
    colId: "videoDeviceAmount3Y",
    headerName: "Total Video Device Bookings $$ (Last 3 Years)",
    subGroupHeader: "Total Video Device Bookings $$",
  },
  {
    colId: "invoiceArrNonCollabSecurity",
    headerName: "Invoice ARR (Other - Security)",
  },
  {
    colId: "invoiceArrNonCollabService",
    headerName: "Invoice ARR (Other - Technical Support / Service)",
  },
  {
    colId: "invoiceArrNonCollabOther",
    headerName: "Invoice ARR (Other - Undefined)",
  },
  {
    colId: "invoiceArrContactCenterCloud",
    headerName: "Invoice ARR (Cloud Contact Center)",
  },
];

const CollectionRevenueCellRenderer: FC<{ data: Account }> = ({ data }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLDivElement>(null);
  const [groupColumns, setGroupColumns] = useState<
    Partial<ColDef & ColGroupDef>[]
  >([]);

  const value = COLLECTION_COLUMN?.filter(
    (col) => col !== "invoiceArrContactCenterCloud"
  )!.reduce((a, b) => a + data?.[b as keyof Account], 0);

  useEffect(() => {
    value && value !== null
      ? "$" +
        value.toLocaleString("en-US", {
          maximumFractionDigits: 2,
          minimumFractionDigits: 2,
        })
      : "$0.00";
  }, [value, data]);

  const columns = useMemo(() => {
    return COLLECTION_COLUMN!
      .sort(
        (a, b) =>
          COLLECTION_COLUMN.findIndex((x) => x === a) -
          COLLECTION_COLUMN.findIndex((x) => x === b)
      )
      .map((x) => labelConfig.find((c) => c.colId === x));
  }, [COLLECTION_COLUMN]);

  const nonCollabColumns = columns?.filter((c) => c?.colId?.includes("Non"));

  useEffect(() => {
    const collabGroup = columns?.filter((c) => !c?.colId?.includes("Non"));
    const result: Partial<ColDef & ColGroupDef>[] = [];
    const groupPart: string[] = [];
    collabGroup?.forEach((item: any) => {
      if (item?.subGroupHeader || item?.subGroupHead) {
        // only for cloud contact center group, other subGroupHeader only for sort order
        if (
          item?.subGroupHeader === "Invoice ARR (Cloud Contact Center)" ||
          item?.subGroupHead === "Invoice ARR (Cloud Contact Center)"
        ) {
          if (
            groupPart.includes(item.subGroupHeader) ||
            groupPart.includes(item.subGroupHead)
          ) {
            const curItem = result.filter(
              (r) =>
                r.headerName === item?.subGroupHeader ||
                r.headerName === item?.subGroupHead
            );
            if (curItem?.length) {
              curItem?.[0]?.children?.push({
                colId: item?.colId,
                headerName: item?.headerName,
              });
            }
          } else {
            const curItem: any = {
              colId: SUB_GROUP_MAPPING.filter(
                (i) =>
                  i.groupHeader === item?.subGroupHeader ||
                  i.groupHeader === item?.subGroupHead
              )?.[0]?.colId,
              headerName: item?.subGroupHeader || item.subGroupHead,
              children: [],
            };
            curItem.children.push({
              colId: item?.colId,
              headerName: item?.headerName,
            });
            groupPart.push(item.subGroupHeader || item.subGroupHead);
            result.push(curItem);
          }
        } else {
          result.push({
            colId: item?.colId,
            headerName: item?.headerName,
          });
        }
      } else {
        if (SUB_GROUP_MAPPING.find((i) => i.colId !== item.colId))
          result.push({
            colId: item?.colId,
            headerName: item?.headerName,
          });
      }
    });
    setGroupColumns(result);
    // console.log(result);
  }, [columns]);

  const popRef = useRef<HTMLDivElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    document.addEventListener("click", closePop);
    return () => {
      document.removeEventListener("click", closePop);
    };
  }, []);

  const closePop = (e: Event) => {
    if (
      !popRef.current?.contains(e.target as Node) &&
      popRef.current !== e.target &&
      !rootRef.current?.contains(e.target as Node)
    ) {
      setAnchorEl(null);
    }
  };

  const handleClick = (event: MouseEvent<HTMLDivElement>) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  return (
    <>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          cursor: "pointer",
        }}
        ref={rootRef}
        onClick={handleClick}
      >
        $
        {value && value !== null
          ? value.toLocaleString("en-US", {
              maximumFractionDigits: 2,
              minimumFractionDigits: 2,
            })
          : "0.00"}
        <KeyboardArrowDown style={{ fontSize: "1rem" }} />
      </div>
      {anchorEl ? (
        <ArrowPopper
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          placement="right-start"
          disablePortal={false}
          modifiers={[
            {
              name: "offset",
              options: {
                offset: [0, 0],
              },
            },
          ]}
          style={{
            zIndex: status ? 0 : 2000,
            marginLeft: -30,
          }}
        >
          <div ref={popRef}>
            <Grid container={true} direction="column">
              <>
                {groupColumns?.length ? (
                  <>
                    <Grid
                      item={true}
                      style={{
                        color: "#333",
                        fontSize: 14,
                        lineHeight: "14px",
                        fontWeight: 700,
                        marginBottom: 10,
                      }}
                    >
                      Collab
                    </Grid>
                    <>
                      {groupColumns?.map(
                        (col: Partial<ColDef & ColGroupDef>, i) => (
                          <div key={col?.colId}>
                            {!!col?.children?.length && (
                              <Divider style={{ margin: "5px 0" }} />
                            )}
                            <Grid
                              item={true}
                              container={true}
                              key={col.colId! + i}
                              justify="space-between"
                              style={{
                                marginTop: i === 0 ? 0 : 4,
                              }}
                              alignItems="center"
                            >
                              <Grid
                                item={true}
                                style={{
                                  color: "#545454",
                                  fontSize: 14,
                                  lineHeight: "19px",
                                }}
                              >
                                {col.headerName!.split(/\((.*)\)/)?.[1] ===
                                "Last 3 Years"
                                  ? "Total Video Device Bookings $$ (Last 3 Years)"
                                  : col.headerName!.split(/\((.*)\)/)?.[1]}
                              </Grid>
                              <Grid
                                item={true}
                                style={{
                                  color: "#333",
                                  fontSize: 14,
                                  lineHeight: "19px",
                                  marginLeft: 16,
                                  fontWeight: 500,
                                }}
                              >
                                {`$${(
                                  data?.[col.colId! as keyof Account] || 0
                                ).toLocaleString("en-US", {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}`}
                              </Grid>
                            </Grid>
                            {!!col?.children?.length && (
                              <Grid
                                item={true}
                                container={true}
                                key={col?.colId}
                                justify="space-between"
                                style={{
                                  display: "flex",
                                  flexDirection: "column",
                                  marginTop: i === 0 ? 0 : 4,
                                }}
                                alignItems="center"
                              >
                                {col?.children?.map(
                                  (child: Partial<ColDef & ColGroupDef>) => (
                                    <div
                                      key={child.colId!}
                                      style={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        width: "100%",
                                      }}
                                    >
                                      <Grid
                                        item={true}
                                        style={{
                                          color: "#898989",
                                          fontSize: 12,
                                          lineHeight: "16px",
                                        }}
                                      >
                                        {
                                          child.headerName!.split(
                                            /\((.*)\)/
                                          )?.[1]
                                        }
                                      </Grid>
                                      <Grid
                                        item={true}
                                        style={{
                                          color: "#333",
                                          fontSize: 12,
                                          lineHeight: "14px",
                                          marginLeft: 16,
                                          fontWeight: 500,
                                        }}
                                      >
                                        $
                                        {(
                                          data?.[
                                            child.colId! as keyof Account
                                          ] || 0
                                        ).toLocaleString("en-US", {
                                          minimumFractionDigits: 2,
                                          maximumFractionDigits: 2,
                                        })}
                                      </Grid>
                                    </div>
                                  )
                                )}
                              </Grid>
                            )}
                            {!!col?.children?.length && (
                              <Divider style={{ margin: "5px 0" }} />
                            )}
                          </div>
                        )
                      )}
                    </>
                    <br />
                  </>
                ) : null}
                {nonCollabColumns?.length ? (
                  <>
                    <Grid
                      item={true}
                      style={{
                        color: "#333",
                        fontSize: 14,
                        lineHeight: "14px",
                        fontWeight: 700,
                        marginBottom: 10,
                      }}
                    >
                      Non-Collab
                    </Grid>
                    {nonCollabColumns?.map((col, i) => (
                      <Grid
                        item={true}
                        container={true}
                        key={col?.colId ? col?.colId + i : i}
                        justify="space-between"
                        style={{ marginTop: i === 0 ? 0 : 4 }}
                        alignItems="center"
                      >
                        <Grid
                          item={true}
                          style={{
                            color: "#545454",
                            fontSize: 14,
                            lineHeight: "19px",
                          }}
                        >
                          {col?.headerName!.split(/\((.*)\)/)?.[1]}
                        </Grid>
                        <Grid
                          item={true}
                          style={{
                            color: "#333",
                            fontSize: 14,
                            lineHeight: "19px",
                            marginLeft: 16,
                            fontWeight: 500,
                          }}
                        >
                          $
                          {(col?.colId
                            ? data?.[col?.colId as keyof Account] || 0
                            : 0
                          ).toLocaleString("en-US", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </Grid>
                      </Grid>
                    ))}
                  </>
                ) : null}
              </>
            </Grid>
          </div>
        </ArrowPopper>
      ) : null}
    </>
  );
};

export default classHOC(CollectionRevenueCellRenderer);
